import { z } from 'zod'
import { ITEM_OCCASIONS, type ItemOccasion } from './enrichment'

export const EXPLORE_MODES_MAX = 8
export const EXPLORE_MODE_NAME_MAX = 40
export const EXPLORE_MODE_DESCRIPTION_MAX = 280
export const EXPLORE_MODE_SPOTS_MAX = 20

/** The icons a mode can wear; the app maps each key to one icon. */
export const EXPLORE_MODE_ICONS = [
  'sparkles',
  'party',
  'briefcase',
  'heart',
  'family',
  'coffee',
  'food',
  'wine',
  'music',
  'sport',
  'nature',
  'culture',
  'friends',
  'study',
  'moon',
  'sun',
] as const
export type ExploreModeIcon = (typeof EXPLORE_MODE_ICONS)[number]

/** What a mode shows in Explore and Search: everything, only events or only places. */
export const EXPLORE_MODE_SHOWS = ['all', 'events', 'places'] as const
export type ExploreModeShow = (typeof EXPLORE_MODE_SHOWS)[number]

/** A starter the app offers prefilled on the first "+"; the app supplies name and description. */
export type ExploreModeTemplate = {
  key: string
  icon: ExploreModeIcon
  occasions: readonly ItemOccasion[]
}

export const EXPLORE_MODE_TEMPLATES = [
  { key: 'party', icon: 'party', occasions: ['party'] },
  { key: 'business', icon: 'briefcase', occasions: ['business', 'coffee'] },
  { key: 'date', icon: 'heart', occasions: ['date', 'food', 'culture'] },
  { key: 'family', icon: 'family', occasions: ['family', 'outdoor'] },
] as const satisfies readonly ExploreModeTemplate[]
export type ExploreModeTemplateKey = (typeof EXPLORE_MODE_TEMPLATES)[number]['key']

/** The template every signed-in account finds in its list once. */
export const EXPLORE_MODE_STARTER_TEMPLATE = 'party' satisfies ExploreModeTemplateKey

const nameSchema = z.string().trim().min(1).max(EXPLORE_MODE_NAME_MAX)
const descriptionSchema = z.string().trim().max(EXPLORE_MODE_DESCRIPTION_MAX)
const occasionsSchema = z.array(z.enum(ITEM_OCCASIONS)).max(ITEM_OCCASIONS.length)
const listingIdsSchema = z.array(z.string().uuid()).max(EXPLORE_MODE_SPOTS_MAX)

/** A go-to place of a mode. */
export const exploreModeSpotSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  city: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
})

/** One of the caller's own modes for Explore and Search; never shown to anyone else. */
export const exploreModeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  icon: z.enum(EXPLORE_MODE_ICONS),
  /** What the person wants in this mode, in their own words; may be empty. */
  description: z.string(),
  /** Occasions of the item sorting this mode favours. */
  occasions: z.array(z.enum(ITEM_OCCASIONS)),
  /** Which kind the mode lets through, on top of the Events or Places filter. */
  show: z.enum(EXPLORE_MODE_SHOWS),
  /** Go-to places, ranked first in Explore wherever they are near. */
  spots: z.array(exploreModeSpotSchema),
  sort: z.number().int(),
})

export const exploreModesResponseSchema = z.object({
  modes: z.array(exploreModeSchema),
})

export const createExploreModeRequestSchema = z.object({
  name: nameSchema,
  icon: z.enum(EXPLORE_MODE_ICONS),
  description: descriptionSchema.optional(),
  occasions: occasionsSchema.optional(),
  /** 'all' when left out. */
  show: z.enum(EXPLORE_MODE_SHOWS).optional(),
  /** Go-to places; ids of places that are gone or not public are left out. */
  spotIds: listingIdsSchema.optional(),
})

export const updateExploreModeRequestSchema = z.object({
  name: nameSchema.optional(),
  icon: z.enum(EXPLORE_MODE_ICONS).optional(),
  description: descriptionSchema.optional(),
  occasions: occasionsSchema.optional(),
  show: z.enum(EXPLORE_MODE_SHOWS).optional(),
})

/** Replaces the go-to places; ids of places that are gone or not public are left out. */
export const setExploreModeSpotsRequestSchema = z.object({
  listingIds: listingIdsSchema,
})

/** Asks for the starter mode; the app sends its name and description in the person's language. */
export const seedExploreModeStarterRequestSchema = z.object({
  template: z.literal(EXPLORE_MODE_STARTER_TEMPLATE),
  name: nameSchema,
  description: descriptionSchema,
})

export type ExploreModeSpotDto = z.infer<typeof exploreModeSpotSchema>
export type ExploreModeDto = z.infer<typeof exploreModeSchema>
export type ExploreModesResponse = z.infer<typeof exploreModesResponseSchema>
export type CreateExploreModeRequest = z.infer<typeof createExploreModeRequestSchema>
export type UpdateExploreModeRequest = z.infer<typeof updateExploreModeRequestSchema>
export type SetExploreModeSpotsRequest = z.infer<typeof setExploreModeSpotsRequestSchema>
export type SeedExploreModeStarterRequest = z.infer<typeof seedExploreModeStarterRequestSchema>
